import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Bot, X, Send, Minimize2, Maximize2, Sparkles, AlertCircle, Download, Flag, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { adminSupabase as supabase } from '@/integrations/supabase/adminClient';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const SUGGESTED_QUESTIONS: Record<string, string[]> = {
  '/admin/applications/individual': ['How do I approve an application?', 'What does EDD Required mean?', 'How do I reject with a reason?'],
  '/admin/applications/corporate': ['How do I verify company documents?', 'What is a KYB check?', 'How do I add a UBO?'],
  '/admin/compliance/command-center': ['What should I do first each morning?', 'How do I clear a screening alert?', 'What does the SLA clock mean?'],
  '/admin/compliance/screening': ['How do I disposition a PEP match?', 'What is a false positive?', 'When should I escalate to EDD?'],
  '/admin/compliance/alerts': ['How do I dismiss an alert?', 'When should I escalate to a case?', 'What does the risk score mean?'],
  '/admin/approvals': ['How does 4-eyes approval work?', 'Who can approve a fund freeze?', 'What happens if I reject an approval?'],
  '/admin/treasury': ['How do I check FX positions?', 'What is the EOD checklist?', 'How do I update spread rules?'],
  '/admin/ledger/safeguarding': ['How do I reconcile a break?', 'What causes safeguarding discrepancies?', 'Who do I notify about a break?'],
  '/admin/ops/incidents': ['How do I log a P1 incident?', 'What is PSD2 reportable?', 'How do I close an incident?'],
  '/admin/compliance/cases': ['How do I open a new case?', 'What does SLA deadline mean?', 'How do I close a case?'],
  '/admin/compliance/edd': ['What triggers an EDD request?', 'What documents do I need?', 'How do I mark a false positive?'],
  '/admin/compliance/sar': ['What is tipping-off?', 'How do I file a SAR?', 'Who approves SAR submissions?'],
  '/admin/ledger/reconciliation': ['How do I match an unreconciled item?', 'What do I do with a break?', 'How do I import a bank statement?'],
  '/admin/payments/outbound': ['What does RJCT mean?', 'How do I retry a failed payment?', 'What is the SEPA cut-off time?'],
  '/admin/cards': ['How do I block a card?', 'How do I reset a PIN?', 'How do I set card limits?'],
  '/admin/clients': ['How do I freeze a client account?', 'What does the risk score mean?', 'How do I update client details?'],
  '/admin/security': ['How do I review an approval request?', 'What is break-glass access?', 'How do I add a user to the allowlist?'],
};

function getSuggestions(path: string): string[] {
  const exact = SUGGESTED_QUESTIONS[path];
  if (exact) return exact;
  const match = Object.keys(SUGGESTED_QUESTIONS)
    .filter(k => path.startsWith(k))
    .sort((a, b) => b.length - a.length)[0];
  if (match) return SUGGESTED_QUESTIONS[match];
  return ['How does this page work?', 'What are the key actions here?', 'What should I do if something goes wrong?'];
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/page-ai-assistant`;

export function PageAIAssistant() {
  const location = useLocation();
  const { adminUser } = useAdminAuth();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasNewMsg, setHasNewMsg] = useState(false);
  const [reportMode, setReportMode] = useState(false);
  const [reportIssue, setReportIssue] = useState('');
  const [reportSeverity, setReportSeverity] = useState<'low' | 'medium' | 'high'>('medium');
  const [reportSent, setReportSent] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Reset conversation when page changes
  useEffect(() => {
    setMessages([]);
    setError(null);
    setReportMode(false);
    setReportSent(false);
  }, [location.pathname]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const suggestions = getSuggestions(location.pathname);

  const send = useCallback(async (text: string) => {
    if (!text.trim() || isStreaming) return;
    setError(null);
    const userMsg: Message = { role: 'user', content: text.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setIsStreaming(true);

    abortRef.current = new AbortController();

    try {
      const resp = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          messages: newMessages,
          currentPage: location.pathname,
        }),
        signal: abortRef.current.signal,
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `Error ${resp.status}`);
      }

      if (!resp.body) throw new Error('No response body');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantContent = '';

      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let newlineIdx: number;
        while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
          let line = buffer.slice(0, newlineIdx);
          buffer = buffer.slice(newlineIdx + 1);
          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || !line.trim()) continue;
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') break;
          try {
            const parsed = JSON.parse(jsonStr);
            const chunk = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (chunk) {
              assistantContent += chunk;
              setMessages(prev =>
                prev.map((m, i) =>
                  i === prev.length - 1 && m.role === 'assistant'
                    ? { ...m, content: assistantContent }
                    : m
                )
              );
            }
          } catch {
            buffer = line + '\n' + buffer;
            break;
          }
        }
      }

      if (!open) setHasNewMsg(true);
    } catch (e: unknown) {
      if ((e as Error)?.name === 'AbortError') return;
      setError((e as Error)?.message || 'Something went wrong. Please try again.');
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && !last.content) return prev.slice(0, -1);
        return prev;
      });
    } finally {
      setIsStreaming(false);
    }
  }, [messages, isStreaming, location.pathname, open]);

  const handleOpen = () => {
    setOpen(true);
    setHasNewMsg(false);
    setMinimized(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const exportConversation = () => {
    const page = location.pathname;
    const date = new Date().toLocaleString();
    let md = `# AI Assistant Session\n\n**Page:** ${page}\n**Date:** ${date}\n\n---\n\n`;
    messages.forEach(m => {
      md += `**${m.role === 'user' ? '👤 You' : '🤖 Assistant'}:**\n\n${m.content}\n\n---\n\n`;
    });
    if (error) md += `**Error reported:** ${error}\n`;
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-session-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const submitReport = async () => {
    if (!reportIssue.trim()) return;
    setReportLoading(true);
    try {
      const { error: insertError } = await supabase.from('ai_dev_reports' as any).insert({
        page_path: location.pathname,
        reporter_user_id: adminUser?.userId || null,
        reporter_email: adminUser?.email || null,
        issue_summary: reportIssue.trim(),
        severity: reportSeverity,
        conversation_context: messages.length > 0
          ? messages.map(m => ({ role: m.role, content: m.content.slice(0, 500) }))
          : null,
        status: 'open',
      });
      if (insertError) throw insertError;
      setReportSent(true);
      setReportMode(false);
      setReportIssue('');
    } catch (e) {
      console.error('Failed to submit report:', e);
      setError('Could not submit report. Please try again.');
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <div className="fixed bottom-[88px] right-6 z-40">
          <button
            onClick={handleOpen}
            title="AI Page Assistant"
            className="h-11 w-11 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center bg-secondary text-secondary-foreground border border-border relative"
          >
            <Sparkles className="h-5 w-5" />
            {hasNewMsg && (
              <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-destructive border-2 border-background" />
            )}
          </button>
        </div>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-6 z-40 w-[340px] flex flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200"
          style={{ height: 'min(520px, calc(100vh - 120px))' }}
        >
          {/* Header */}
          <div className="bg-foreground text-background px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 shrink-0" />
              <div>
                <p className="font-semibold text-sm leading-tight">AI Assistant</p>
                <p className="text-[10px] opacity-60 leading-tight">Page-aware help</p>
              </div>
              <Badge variant="outline" className="ml-1 text-[9px] border-background/30 text-background/70 h-4 px-1 bg-transparent">
                BETA
              </Badge>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && !reportMode && (
                <>
                  <button
                    onClick={exportConversation}
                    title="Export conversation"
                    className="opacity-60 hover:opacity-100 p-1 rounded transition-opacity"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => { setReportMode(true); setReportSent(false); }}
                    title="Report issue to dev team"
                    className="opacity-60 hover:opacity-100 p-1 rounded transition-opacity"
                  >
                    <Flag className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
              <button
                onClick={() => setMinimized(m => !m)}
                className="opacity-60 hover:opacity-100 p-1 rounded transition-opacity"
              >
                {minimized ? <Maximize2 className="h-3.5 w-3.5" /> : <Minimize2 className="h-3.5 w-3.5" />}
              </button>
              <button
                onClick={() => setOpen(false)}
                title="Close"
                className="opacity-80 hover:opacity-100 p-1.5 rounded-md transition-opacity hover:bg-background/20 ml-0.5"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {!minimized && (
            <div className="flex flex-col flex-1 min-h-0">
              {/* Report mode */}
              {reportMode ? (
                <div className="p-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold mb-1">Report an Issue</p>
                    <p className="text-xs text-muted-foreground">Describe the problem and it will be sent to the development team with the conversation context.</p>
                  </div>
                  <Textarea
                    value={reportIssue}
                    onChange={e => setReportIssue(e.target.value)}
                    placeholder="Describe the issue in detail..."
                    className="text-xs min-h-[80px] resize-none"
                    disabled={reportLoading}
                  />
                  <div className="space-y-1">
                    <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Severity</p>
                    <div className="flex gap-2">
                      {(['low', 'medium', 'high'] as const).map(s => (
                        <button
                          key={s}
                          onClick={() => setReportSeverity(s)}
                          className={cn(
                            'flex-1 text-xs px-2 py-1 rounded-lg border transition-colors capitalize',
                            reportSeverity === s
                              ? s === 'high' ? 'bg-red-500/20 border-red-500/40 text-red-600 dark:text-red-400'
                                : s === 'medium' ? 'bg-amber-500/20 border-amber-500/40 text-amber-600 dark:text-amber-400'
                                : 'bg-emerald-500/20 border-emerald-500/40 text-emerald-600 dark:text-emerald-400'
                              : 'border-border text-muted-foreground hover:bg-muted/50'
                          )}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 text-xs"
                      onClick={() => setReportMode(false)}
                      disabled={reportLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 text-xs"
                      onClick={submitReport}
                      disabled={!reportIssue.trim() || reportLoading}
                    >
                      {reportLoading ? 'Sending…' : 'Send Report'}
                    </Button>
                  </div>
                </div>
              ) : (
              <>
                  {/* Success toast */}
                  {reportSent && (
                    <div className="mx-3 mt-3 flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400">
                      <CheckCircle className="h-3.5 w-3.5 shrink-0" />
                      Report submitted to the dev team.
                    </div>
                  )}

                  {/* Messages */}
                  <ScrollArea className="flex-1 min-h-0">
                    <div className="p-3 space-y-3">
                      {messages.length === 0 && (
                        <div className="space-y-3">
                          <div className="flex items-start gap-2">
                            <div className="h-6 w-6 rounded-full bg-foreground flex items-center justify-center shrink-0 mt-0.5">
                              <Bot className="h-3.5 w-3.5 text-background" />
                            </div>
                            <div className="bg-muted rounded-xl rounded-tl-sm px-3 py-2 text-xs text-foreground leading-relaxed max-w-[260px]">
                              Hello! I'm your AI assistant for this page. Ask me anything — how things work, what buttons do, or how to fix an issue.
                            </div>
                          </div>
                          <div className="pl-8 space-y-1.5">
                            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                              Suggested questions
                            </p>
                            {suggestions.map(q => (
                              <button
                                key={q}
                                onClick={() => send(q)}
                                disabled={isStreaming}
                                className="block w-full text-left text-xs text-primary hover:text-primary/80 hover:bg-primary/5 px-2 py-1.5 rounded-lg border border-primary/20 transition-colors disabled:opacity-50"
                              >
                                {q}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {messages.map((msg, i) => (
                        <div
                          key={i}
                          className={cn(
                            'flex items-start gap-2',
                            msg.role === 'user' && 'flex-row-reverse'
                          )}
                        >
                          {msg.role === 'assistant' && (
                            <div className="h-6 w-6 rounded-full bg-foreground flex items-center justify-center shrink-0 mt-0.5">
                              <Bot className="h-3.5 w-3.5 text-background" />
                            </div>
                          )}
                          <div
                            className={cn(
                              'px-3 py-2 rounded-xl text-xs leading-relaxed max-w-[260px] whitespace-pre-wrap',
                              msg.role === 'user'
                                ? 'bg-primary text-primary-foreground rounded-tr-sm'
                                : 'bg-muted text-foreground rounded-tl-sm'
                            )}
                          >
                            {msg.content || (
                              <span className="flex gap-1 items-center">
                                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:0ms]" />
                                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:150ms]" />
                                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:300ms]" />
                              </span>
                            )}
                          </div>
                        </div>
                      ))}

                      {error && (
                        <div className="flex items-start gap-2 text-destructive">
                          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                          <p className="text-xs">{error}</p>
                        </div>
                      )}

                      <div ref={bottomRef} />
                    </div>
                  </ScrollArea>

                  {/* Input */}
                  <div className="border-t px-3 py-2 shrink-0 bg-card">
                    <div className="flex gap-2 items-end">
                      <Textarea
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask anything about this page…"
                        className="flex-1 min-h-[36px] max-h-[100px] text-xs resize-none"
                        disabled={isStreaming}
                        rows={1}
                      />
                      <Button
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        onClick={() => send(input)}
                        disabled={!input.trim() || isStreaming}
                      >
                        <Send className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <p className="text-[9px] text-muted-foreground/50 mt-1.5 text-center">
                      Shift+Enter for new line · <button className="underline hover:opacity-80" onClick={() => setReportMode(true)}>Report an issue</button>
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
